import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import type {UserMailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import type {SelectedMailingListsMap} from '@Components/email-maker-show-mailing-lists-panel';
import type {
  ResponseAudience
} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience-thunk';
import type {MailingListsResponse} from '@Components/mailing-list-contacts/mailing-list-contacts-thunk';
import type {SignupFormPublishStatus} from '@FormMakerReact/email-signup-form.types';

export const contactsGridContainerQuery: ContainerQueryObject = {
  xsmall: {
    maxWidth: 460,
  },
  small: {
    maxWidth: 700,
  },
  medium: {
    minWidth: 461,
    maxWidth: 884,
  },
  large: {
    minWidth: 885,
  },
  xlarge: {
    minWidth: 1020,
  },
  xxlarge: {
    minWidth: 1420,
  },
};

export interface ContactsContainerQuery {
  isExtraSmallScreen?: boolean;
  isSmallScreen?: boolean;
  isMediumScreen?: boolean;
  isLargeScreen?: boolean;
  isExtraLargeScreen?: boolean;
}

export type SignupFormEmailsUpdateInformation = Record<string, number>;

export enum ContactsTabSelection {
  SIGNUP_FORMS = 'SIGNUP_FORMS',
  MAILING_LISTS = 'MAILING_LISTS',
}

export const CONTACTS_TABS_ID = 'ContactsTypeTabs';
export const MAILING_LIST_TYPE_TABS = 'MailingListTypeTabs';

export enum MailingListsMenuSelection {
  USER_CREATED_MAILING_LISTS = 'USER_CREATED_MAILING_LISTS',
  SMART_MAILING_LISTS = 'SMART_MAILING_LISTS',
}

export const TABS_ID = 'mystuff-contactsTab';

export type MailingListContact = string;
export type MailingListContacts = MailingListContact[];

export enum SmartMailingListsViewType {
  VERTICAL_STACK,
  HORIZONTAL_LIST,
}

export enum SmartMailingListTypes {
  LEAST_ENGAGED = 'LEAST_ENGAGED',
  MOST_ENGAGED = 'MOST_ENGAGED',
  ALL_CONTACTS = 'ALL_CONTACTS',
  EVENT_ATTENDEES = 'EVENT_ATTENDEES',
  SOMEWHAT_ENGAGED = 'SOMEWHAT_ENGAGED',
}

export enum ContactsActivityIndicatorAccessPoints {
  MYSTUFF_SIDEBAR,
  SIGNUP_FORMS_TAB,
}

export interface WizardMailingListsFetchResponse {
  allMailingLists: UserMailingList[];
  selectedMailingListsMap: SelectedMailingListsMap;
}

export interface MailingListFetchResponse {
  mailingList: UserMailingList;
}

export type SmartMailingListTooltipMap = Record<SmartMailingListTypes, number>;
export const CONTACTS_CONTROLS_HEADER_CLASS = 'js-contacts-controls-header';

export const MY_STUFF_NAV_CLASS = 'js-mystuff-nav-container-inner';
export const PMW_PRE_SELECTED_MAILING_LIST_ID = 'PMWMID';
export const mailingListPanelRefreshEvent = 'mailingListsPanel::refresh';
export const signupFormPublishStatusChangedEvent = 'signupFormPublishStatusChanged';
export const PMW_PRE_SELECTED_MAILING_LIST_ID_COOKIE_TTL_MINUTES = 5;
export const HAS_SEEN_CONTACTS_SIDEBAR_INTRO_POPUP = 'pmwcontactssidebarintropopup';
export const SIDEBAR_ACTIVITY_INDICATOR_TIME = 'pmwcontactssidebaractivity';
export const SIGNUP_FORM_ITEM_INDICATOR_TIME = 'pmwsignupformitemindicator';
export const mailingListOrder: SmartMailingListTypes[] = [
  SmartMailingListTypes.ALL_CONTACTS,
  SmartMailingListTypes.EVENT_ATTENDEES,
  SmartMailingListTypes.MOST_ENGAGED,
  SmartMailingListTypes.SOMEWHAT_ENGAGED,
  SmartMailingListTypes.LEAST_ENGAGED,
];

export type ContactsInactivityCallbacks = {
  onInactive: () => void;
  onActive?: () => void;
};

export interface GetUserCreatedMailingListsAjaxResponse extends Omit<MailingListsResponse, 'mailingLists'> {
  mailingLists: MailingListWithResponseAudiences[];
}

export interface MailingListWithResponseAudiences extends Omit<UserMailingList, 'mailingListContacts'> {
  mailingListContacts?: ResponseAudience[];
}

export interface SignupFormPublishStatusChangedEventDetail {
  idEmailSignupForm: string;
  publishStatus: Partial<SignupFormPublishStatus>;
}
